//@flow
import { Alert, AlertTitle } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import DialogContentText from "@mui/material/DialogContentText";
import Grid from "@mui/material/Grid2";
import Stack from "@mui/material/Stack";
import { DataGridPremium, GridActionsCellItem, GridToolbar } from '@mui/x-data-grid-premium';
import { enqueueSnackbar } from "notistack";
import React, { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from "react-i18next";
import PrintIcon from "../../../../../assets/images/icons/buttons/printIcon";
import SendIcon from "../../../../../assets/images/icons/buttons/sendIcon";
import TrashIcon from "../../../../../assets/images/icons/buttons/trashIcon";
import ActionStatus from "../../../../../components/app/alerts/actionStatus/actionStatus";
import ConfirmDialog from "../../../../../components/app/dialogs/confirmDialog/confirmDialog";
import PrintBadgeButton from "../../../../../components/app/printBadgeButton/printBadgeButton";
import ContentLoader from "../../../../../components/general/contentLoader/contentLoader";
import {
	CustomPagination
} from "../../../../../components/grids/dataGridCustom/pagination/customPaginations/customPagination";
import DataGridWrapper from "../../../../../components/grids/dataGridWrapper/dataGridWrapper";
import { CatalogueLanguages, PaperSize, PartnerTypes, Permissions } from "../../../../../helpers/constants";
import { EventStatus } from "../../../../../helpers/date";
import { muiGridLocales } from "../../../../../helpers/muiGridLocales";
import { dialogueContextTextDefaults } from "../../../../../helpers/theme/defaultProps";
import useFetchBadgesAndPrintToPdf from "../../../../../hooks/badges/useFetchBadgesAndPrintToPdf";
import { useExhibition } from "../../../../../hooks/exhibitions/useExhibition";
import useExhibitionAction from "../../../../../hooks/exhibitions/useExhibitionAction";
import useExhibitionData from "../../../../../hooks/exhibitions/useExhibitionData";
import useUserData from "../../../../../hooks/user/useUserData";
import { EXHIBITOR_PROFILE } from "../../../../../locales/components/namespaces";
import { useHasPermission } from "../../../../../modules/permission/hooks/useHasPermission";
import type { Exhibition, Partner } from "../../../../../types/exhibition/types";
import AddPartnerDialog from "../addPartner/addPartner";
import ExcelImport from "./excelImport";
import { SendPopOver } from "./sendPopOver";

type Props = {
	exhibitorId: string,
	exhibitionId: string,
	partners: Array<Partner>,
}

type InnerProps = Props & {
	exhibition: Exhibition
}

const PartnersInner = (props: InnerProps) => {
	const { partners, exhibition, exhibitionId, exhibitorId } = props
	const { language } = useUserData()
	const isOrganizer = useHasPermission([Permissions.BeOrganizer], { shouldView: true })
	const { t } = useTranslation(EXHIBITOR_PROFILE)
	const [showFileUpload, setShowFileUpload] = useState(false)
	const [showAddDialog, setShowAddDialog] = useState(false)
	const [selectionModel, setSelectionModel] = useState([])
	const [partnerIdToRemove, setPartnerIdToRemove] = useState(null)
	const [sendPopOver, setSendPopOver] = useState({
		anchorEl: null,
		open: false,
	})
	const { actionOnPartnerStatus, loading } = useExhibitionData()
	const {
		removePartner,
		clearActionOnPartnerStatus,
		sendPartnerBadges,
		clearBulkPartnersStatus
	} = useExhibitionAction()
	const {loading: loadingBadges, fetchBadgesAndPrintToPdf} = useFetchBadgesAndPrintToPdf()
	const handleSendPopOver = (value) => {
		setSendPopOver({ ...value })
	}
	
	const onRemove = () => {
		removePartner(exhibitionId, exhibitorId, partnerIdToRemove)
		setPartnerIdToRemove(null)
	}
	const onRemoveButtonClick = (id) => {
		setPartnerIdToRemove(id)
	}
	const onSendBadge = (values) => {
		sendPartnerBadges(selectionModel, values.email)
	}
	useEffect(() => {
		if (actionOnPartnerStatus && actionOnPartnerStatus.type === "success") {
			setSelectionModel([])
		}
	}, [actionOnPartnerStatus])
	const printSize = props.type === PartnerTypes.Partner ? exhibition.partnerBadgeTemplateTypeId :
		props.type === PartnerTypes.ServiceProvider ? exhibition.providerBadgeTemplateTypeId :
			props.type === PartnerTypes.Contractor ? exhibition.contractorBadgeTemplateTypeId :
				props.type === PartnerTypes.ExhibitionStaff ? exhibition.staffBadgeTemplateTypeId :
					props.type === PartnerTypes.Organizer ? exhibition.organizerBadgeTemplateTypeId : PaperSize.A4
	
	const columns = useMemo(() => {
		const result = [
			...(exhibition.catalogueLanguage !== CatalogueLanguages.EN ? [{
				field: 'name',
				headerName: t('Name (EL)'),
				width: 180
			}] : []),
			...(exhibition.catalogueLanguage !== CatalogueLanguages.EL ? [{
				field: 'nameEn',
				headerName: t('Name (EN)'),
				width: 180
			}] : []),
			{ field: 'email', headerName: t('Email'), width: 200 },
			...(props.type === PartnerTypes.ServiceProvider || props.type === PartnerTypes.ExhibitionStaff || props.type === PartnerTypes.Contractor ? [
				{ field: 'companyName', headerName: t('Company Name'), width: 200, flex: 1 }
			] : []),
			{
				field: 'id',
				type: 'actions',
				headerName: t('Badge'),
				minWidth: 80,
				renderCell: (({ value }) => (
					<PrintBadgeButton
						printSize={printSize}
						userId={value}
						type={"partner"}
					/>
				))
			},
		]
		if (exhibition.status !== EventStatus.EXPIRED) {
			result.push(({
				headerName: t('Actions'),
				field: 'actions',
				type: 'actions',
				width: 100,
				getActions: (params) => [
					<GridActionsCellItem
						onClick={() => onRemoveButtonClick(params.id)}
						icon={<TrashIcon/>}
						label={t("Remove")}
						color={"error"}
					/>,
				],
			}))
		}
		return result
	}, [exhibition.catalogueLanguage, exhibition.status, printSize, props.type, t])
	const limitedNumberOfPartners = !isNaN(exhibition.numberOfPartnersPerExhibitor) && exhibition.numberOfPartnersPerExhibitor > 0
	const canAddPartner = !limitedNumberOfPartners || (limitedNumberOfPartners && (exhibition.numberOfPartnersPerExhibitor >= partners.length + 1))
	const addButtonText = props.type === PartnerTypes.Partner ? 'Add a partner' :
		props.type === PartnerTypes.ServiceProvider ? 'Add a service provider' :
			props.type === PartnerTypes.Contractor ? 'Add a contractor' :
				props.type === PartnerTypes.ExhibitionStaff ? 'Add exhibition staff' : 'Add organizer'
	const selectionButtonText = props.type === PartnerTypes.Partner ? 'Send badge to {{count}} partners'
		: props.type === PartnerTypes.ServiceProvider ? 'Send badge to {{count}} service providers'
			: props.type === PartnerTypes.Contractor ? 'Send badge to {{count}} contractors' :
				props.type === PartnerTypes.ExhibitionStaff ? 'Send badge to {{count}} exhibition staff' : 'Send badge to {{count}} organizers'
	const printBadgeButtonText = props.type === PartnerTypes.Partner ? 'Print badges of {{count}} partners' :
		props.type === PartnerTypes.ServiceProvider ? 'Print badges of {{count}} service providers' :
			props.type === PartnerTypes.Contractor ? 'Print badges of {{count}} contractors' :
				props.type === PartnerTypes.ExhibitionStaff ? 'Print badges of {{count}} exhibition staff' : 'Print badges of {{count}} organizers'
	return (
		<Box>
			{exhibition.status !== EventStatus.EXPIRED && (
				<React.Fragment>
					{(limitedNumberOfPartners && !isOrganizer) && (
						<Alert
							severity="info"
							sx={{
								borderRadius: '12px',
								background: '#E0F2FE',
								borderLeft: '3px solid #008AD2',
								color: '#5E666B',
								fontSize: '16px',
								lineHeight: '24px',
								mb: 2
							}}
						>
							<AlertTitle>{t('Info')}</AlertTitle>
							<Trans
								i18nKey={'partner_limit'}
								ns={EXHIBITOR_PROFILE}
								values={{
									maximum: exhibition.numberOfPartnersPerExhibitor,
									remainder: canAddPartner ?
										exhibition.numberOfPartnersPerExhibitor - partners.length : 0
								}}
								defaults={"You can add <bold>maximum of {{maximum}}</bold> people. <bold>Remaining {{remainder}}</bold>"}
								components={{ bold: <strong/> }}
							/>
						</Alert>
					)}
					<Grid container spacing={2} sx={{ mb: 2 }}>
						<Grid size={12} display={'flex'} alignItems={'end'}>
							<Stack direction={{ xs: 'column', sm: 'row', md: 'row', lg: 'row' }} spacing={1}
								   width={'100%'} justifyContent={'end'} gap={1}>
								<Button
									loading={loadingBadges}
									disabled={!(selectionModel && selectionModel.length > 0)}
									variant={"outlined"}
									color={"highlight"}
									startIcon={<PrintIcon color={'#647DFF'}/>}
									size={"small"}
									onClick={() => {
										if(selectionModel && selectionModel.length > 200) {
											enqueueSnackbar(t('You can print maximum of 200 partner badges at once!'), { variant: 'warning' })
										} else {
											fetchBadgesAndPrintToPdf({
												printSize: printSize,
												params: {
													ids: selectionModel?.toString()
												},
												type: "partner"
											})
										}
									}}
								>
									{t(printBadgeButtonText, {
										count: selectionModel.length
									})}
								</Button>
								<Button
									disabled={!(selectionModel && selectionModel.length > 0)}
									onClick={(evt) => setSendPopOver({
										anchorEl: evt.currentTarget,
										open: true,
									})}
									startIcon={<SendIcon stroke={"#fff"}/>}
									variant={"contained"}
									color={"highlight"}
									size={"small"}
								>
									{t(selectionButtonText, {
										count: selectionModel.length
									})}
								</Button>
							
							</Stack>
						</Grid>
					</Grid>
				</React.Fragment>
			)}
			<Stack
				direction="row"
				spacing={2}
				sx={{
					background: '#fff',
					borderTopLeftRadius: '8px',
					borderTopRightRadius: '8px',
					padding: '17px 32px',
					border: '1px solid #E8EBEC',
				}}
			>
				<Button
					disabled={!canAddPartner && !isOrganizer}
					variant={"outlined"}
					size={"small"}
					color={"highlight"}
					onClick={() => setShowFileUpload(true)}
				>
					{t('Import from excel')}
				</Button>
				<Button
					disabled={!canAddPartner && !isOrganizer}
					size={"small"}
					variant={"contained"}
					color={"highlight"}
					onClick={() => setShowAddDialog(true)}
				>
					{t(addButtonText)}
				</Button>
			</Stack>
			<DataGridWrapper>
				{columns && columns.length > 0 && (
					<DataGridPremium
						disableRowGrouping
						disableAggregation
						sx={{ bgcolor: 'background.paper' }}
						initialState={{ density: "standard" }}
						rows={partners}
						columns={columns}
						slots={{
							toolbar: GridToolbar,
							pagination: CustomPagination
						}}
						slotProps={{
							toolbar: {
								csvOptions: { disableToolbarButton: true },
								printOptions: { disableToolbarButton: true }
							}
						}}
						localeText={muiGridLocales(language)}
						paginationModel={{ page: 1, pageSize: 40 }}
						pageSizeOptions={[5, 10, 20, 40, 60]}
						checkboxSelection={exhibition.status !== EventStatus.EXPIRED}
						rowSelectionModel={selectionModel}
						onRowSelectionModelChange={(newSelectionModel) => exhibition.status !== EventStatus.EXPIRED && setSelectionModel(newSelectionModel)}
					/>
				)}
			</DataGridWrapper>
			<ExcelImport
				exhibitorId={exhibitorId}
				exhibitionId={exhibitionId}
				show={showFileUpload}
				onClose={() => {
					setShowFileUpload(false);
					clearBulkPartnersStatus();
				}}
				type={props.type}
			/>
			<AddPartnerDialog
				canAddPartner={canAddPartner}
				catalogueLanguage={exhibition?.catalogueLanguage}
				exhibitorId={exhibitorId}
				exhibitionId={exhibitionId}
				open={showAddDialog}
				onClose={() => setShowAddDialog(false)}
				type={props.type}
				addButtonText={addButtonText}
			/>
			<SendPopOver
				sendPopOver={sendPopOver}
				handleSendPopOver={handleSendPopOver}
				selectionModel={selectionModel}
				loading={loading}
				onSendBadge={onSendBadge}
				type={props.type}
			/>
			<ConfirmDialog
				hasBtnIcons={false}
				onYes={onRemove}
				onNo={() => setPartnerIdToRemove(false)}
				title={t('Confirm Delete')}
				open={Boolean(partnerIdToRemove)}
				handleDialog={(newValue) => setPartnerIdToRemove(newValue)}
			>
				<DialogContentText
					{...dialogueContextTextDefaults}
				>
					{t('This action is permanent. Press OK if you want to proceed.')}
				</DialogContentText>
			</ConfirmDialog>
			<ActionStatus
				status={actionOnPartnerStatus}
				onClose={clearActionOnPartnerStatus}
			/>
		</Box>
	)
}

const Partners = (props) => {
	const { exhibition, loading } = useExhibition()
	return (
		<React.Fragment>
			{exhibition ? (
				<PartnersInner
					{...props}
					exhibition={exhibition}
				/>
			) : (
				<ContentLoader
					loading={loading}
				/>
			)}
		</React.Fragment>
	)
}

export default Partners;
